import Button from '@jetbrains/ring-ui/components/button/button'
import ButtonGroup from '@jetbrains/ring-ui/components/button-group/button-group'
import type {ChangeEvent} from 'react'
import React, {memo, useCallback} from 'react'

import Markdown, {LinkOpenPolicy} from '../../../../../../../../../../common/Markdown/Markdown'
import {useStoredSettingsValue} from '../../../../../../../../contexts/StoredSettingsValue/StoredSettingsValueContext'
import {isPipelinesDockerfileEnabled} from '../../../../../../../../utils/featureToggles'
import Details from '../../../../../../Details/Details'
import TogglableProperty from '../TogglableProperty/TogglableProperty'

import {
  DESCRIPTION,
  DOCKER_IMAGE_PROPERTY_NAME,
  DOCKERFILE_PATH_PROPERTY_NAME,
  DOCKERFILE_SOURCE_PROPERTY_NAME,
  LABEL,
} from './JobStepRunInDocker.consts'
import {useStoredDockerData} from './JobStepRunInDocker.hooks'
import type {JobStepRunInDockerProps} from './JobStepRunInDocker.types'
import {getDockerValues} from './JobStepRunInDocker.utils'
import JobStepRunInDockerDockergile from './JobStepRunInDockerDockerfile/JobStepRunInDockerDockerfile'
import JobStepRunInDockerImageSelect from './JobStepRunInDockerImageSelect/JobStepRunInDockerImageSelect'

import styles from './JobStepRunInDocker.css'

import {StepTypes} from 'src/components/pages/PipelinesPages/types'

function JobStepRunInDocker(props: JobStepRunInDockerProps) {
  const {step, stepIndex, onStepPropertiesChange} = props
  const type = step.type
  const toggleable = step.type !== StepTypes.NODE

  const {setValueToStoredSettings} = useStoredSettingsValue()
  const {
    storedDockerImageKey,
    storedDockerfileSourceKey,
    storedDockerfilePathKey,
    storedDockerImageValue,
    storedDockerfileSourceValue,
    storedDockerfilePathValue,
  } = useStoredDockerData(type, stepIndex)
  const {dockerImageValue, dockerfileSourceValue, dockerfilePathValue} = getDockerValues(step)

  const isToggleChecked =
    dockerImageValue != null || dockerfileSourceValue != null || dockerfilePathValue != null

  const isImageMode =
    (dockerfileSourceValue == null && dockerfilePathValue == null) || !isPipelinesDockerfileEnabled

  const onToggleImageMode = useCallback(() => {
    setValueToStoredSettings(
      storedDockerfilePathKey,
      dockerfilePathValue ?? storedDockerfilePathValue,
    )
    setValueToStoredSettings(
      storedDockerfileSourceKey,
      dockerfileSourceValue ?? storedDockerfileSourceValue,
    )
    onStepPropertiesChange(stepIndex, [
      {name: DOCKER_IMAGE_PROPERTY_NAME, value: storedDockerImageValue},
      {name: DOCKERFILE_PATH_PROPERTY_NAME, value: null},
      {name: DOCKERFILE_SOURCE_PROPERTY_NAME, value: null},
    ])
  }, [
    dockerfilePathValue,
    dockerfileSourceValue,
    onStepPropertiesChange,
    setValueToStoredSettings,
    stepIndex,
    storedDockerImageValue,
    storedDockerfilePathKey,
    storedDockerfilePathValue,
    storedDockerfileSourceKey,
    storedDockerfileSourceValue,
  ])

  const onToggleDockerfileMode = useCallback(() => {
    const properties = []

    setValueToStoredSettings(storedDockerImageKey, dockerImageValue ?? storedDockerImageValue)
    properties.push({name: DOCKER_IMAGE_PROPERTY_NAME, value: null})

    if (storedDockerfilePathValue && !storedDockerfileSourceValue) {
      properties.push({name: DOCKERFILE_PATH_PROPERTY_NAME, value: storedDockerfilePathValue})
    } else {
      properties.push({name: DOCKERFILE_SOURCE_PROPERTY_NAME, value: storedDockerfileSourceValue})
    }
    onStepPropertiesChange(stepIndex, properties)
  }, [
    dockerImageValue,
    onStepPropertiesChange,
    setValueToStoredSettings,
    stepIndex,
    storedDockerImageKey,
    storedDockerImageValue,
    storedDockerfilePathValue,
    storedDockerfileSourceValue,
  ])

  const renderDetails = (
    <Details>
      <Markdown className={styles.description} linkOpenPolicy={LinkOpenPolicy.ALL_IN_NEW_TAB}>
        {DESCRIPTION}
      </Markdown>
    </Details>
  )

  const renderContent = (
    <>
      {isPipelinesDockerfileEnabled && (
        <ButtonGroup className={styles.modeToggle}>
          <Button
            className={styles.modeToggleButton}
            active={isImageMode}
            onClick={onToggleImageMode}
          >
            {'Docker Image'}
          </Button>
          <Button
            className={styles.modeToggleButton}
            active={!isImageMode}
            onClick={onToggleDockerfileMode}
          >
            {'Dockerfile'}
          </Button>
        </ButtonGroup>
      )}

      {isImageMode ? (
        <JobStepRunInDockerImageSelect {...props} />
      ) : (
        <JobStepRunInDockerDockergile {...props} />
      )}
    </>
  )

  const onToggleChange = useCallback(
    ({currentTarget}: ChangeEvent<HTMLInputElement>) => {
      const {checked} = currentTarget
      const properties = []

      if (!checked) {
        setValueToStoredSettings(
          storedDockerImageKey,
          dockerImageValue ?? storedDockerImageValue ?? null,
        )
        setValueToStoredSettings(
          storedDockerfileSourceKey,
          dockerfileSourceValue ?? storedDockerfileSourceValue ?? null,
        )
        setValueToStoredSettings(
          storedDockerfilePathKey,
          dockerfilePathValue ?? storedDockerfilePathValue ?? null,
        )
        properties.push(
          {name: DOCKER_IMAGE_PROPERTY_NAME, value: null},
          {name: DOCKERFILE_SOURCE_PROPERTY_NAME, value: null},
          {name: DOCKERFILE_PATH_PROPERTY_NAME, value: null},
        )
      } else if (
        (!storedDockerImageValue && !storedDockerfileSourceValue && !storedDockerfilePathValue) ||
        storedDockerImageValue
      ) {
        properties.push({name: DOCKER_IMAGE_PROPERTY_NAME, value: storedDockerImageValue})
      } else if (storedDockerfileSourceValue) {
        properties.push({name: DOCKERFILE_SOURCE_PROPERTY_NAME, value: storedDockerfileSourceValue})
      } else if (storedDockerfilePathValue) {
        properties.push({name: DOCKERFILE_PATH_PROPERTY_NAME, value: storedDockerfilePathValue})
      }

      onStepPropertiesChange(stepIndex, properties)
    },
    [
      dockerImageValue,
      dockerfilePathValue,
      dockerfileSourceValue,
      onStepPropertiesChange,
      setValueToStoredSettings,
      stepIndex,
      storedDockerImageKey,
      storedDockerImageValue,
      storedDockerfilePathKey,
      storedDockerfilePathValue,
      storedDockerfileSourceKey,
      storedDockerfileSourceValue,
    ],
  )

  return (
    <div className={styles.container}>
      {toggleable ? (
        <TogglableProperty
          label={LABEL}
          details={renderDetails}
          onChange={onToggleChange}
          checked={isToggleChecked}
        >
          {renderContent}
        </TogglableProperty>
      ) : (
        renderContent
      )}
    </div>
  )
}

export default memo<JobStepRunInDockerProps>(JobStepRunInDocker)
